import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card } from "reactstrap";
import {
  Button,
  ConfigProvider,
  Empty,
  Progress,
  Skeleton,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import { updateMenuData } from "../../../../store/slices/misc/menuData";
import TopMenu from "../../../../route/topMenu";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { useWbsName } from "../../shared/useWbsCodes";
import {
  PnlDetailTab,
  PnlTaskRow,
  fmtINR,
  fmtINRCompact,
  fmtMonthLong,
  fmtQty,
  num,
  perUnit,
} from "./pnlShared";

// Month drill-down for the Monthly Budget P&L — a SEPARATE SCREEN (not a
// drawer) because one month can touch 900+ tasks. Tasks tab is
// server-paginated (50/page) with an activity rollup chip strip; the item tabs
// are bounded lists, client-paginated.

const PAGE_SIZE = 50;

const statChip = (label: string, value: number, color: string) => (
  <Tooltip title={fmtINR(value)} key={label}>
    <div
      style={{
        background: "#f7f9fc",
        border: "1px solid #e3e9f3",
        borderRadius: 8,
        padding: "6px 14px",
        minWidth: 130,
      }}
    >
      <div style={{ fontSize: 11.5, fontWeight: 600, color: "#8094ae" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>
        {fmtINRCompact(value)}
      </div>
    </div>
  </Tooltip>
);

const costCell = (value: string | number) => (
  <Tooltip title={fmtINR(value)}>
    <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINRCompact(value)}</span>
  </Tooltip>
);

const boldCostCell = (value: string | number) => (
  <Tooltip title={fmtINR(value)}>
    <b style={{ fontVariantNumeric: "tabular-nums", color: "#022c57" }}>
      {fmtINRCompact(value)}
    </b>
  </Tooltip>
);

// "Manpower" here = PRW & sub-contract crews + task labour resources (staff
// salaries are a separate future head) — surfaced as a header tooltip.
const manpowerColumnTitle = (
  <Tooltip title="PRW & sub-contract crews (applied manpower norms) + task labour resources, scaled to the month's share. Staff salaries are tracked separately (overhead data push).">
    <span style={{ textDecoration: "underline dotted #b6c2d9", textUnderlineOffset: 3 }}>
      Manpower
    </span>
  </Tooltip>
);

const marginCell = (value: string | number) => (
  <Tooltip title={fmtINR(value)}>
    <span
      style={{
        fontVariantNumeric: "tabular-nums",
        color: num(value) >= 0 ? "#237804" : "#cf1322",
      }}
    >
      {fmtINRCompact(value)}
    </span>
  </Tooltip>
);

// One activity's tasks for the month — fetched on expand, server-paginated so
// a 350-task month never ships whole.
const ActivityTasksTable: React.FC<{
  projectId: number | string;
  month: string;
  phaseId?: number;
  activityId: number;
}> = ({ projectId, month, phaseId, activityId }) => {
  const [rows, setRows] = useState<PnlTaskRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    threeMReportApis
      .getMonthlyPnlMonthTasks(projectId, month, {
        phaseId,
        activityId,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((response) => {
        if (cancelled) return;
        if (response?.status) {
          setRows(response?.data?.tasks || []);
          setCount(num(response?.data?.count));
        } else {
          message.error(response?.message || "Failed to load activity tasks");
        }
      })
      .catch(() => !cancelled && message.error("Failed to load activity tasks"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, month, phaseId, activityId, page]);

  const columns: any[] = [
    { title: "Task ID", dataIndex: "taskSequenceId", key: "seq", width: 150, ellipsis: true },
    { title: "Task", dataIndex: "taskName", key: "name", width: 220, ellipsis: true },
    {
      title: "Month Qty",
      key: "qty",
      width: 130,
      align: "right" as const,
      render: (_: any, row: PnlTaskRow) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtQty(row.scheduledQty)} {row.workUom}
        </span>
      ),
    },
    {
      title: "Share",
      key: "share",
      width: 110,
      render: (_: any, row: PnlTaskRow) => (
        <Progress
          percent={Math.min(100, Math.round(num(row.monthShare) * 100))}
          size="small"
          strokeColor="#2557d6"
        />
      ),
    },
    { title: "Inflow", dataIndex: "inflowAmount", key: "inflow", width: 110, align: "right" as const, render: costCell },
    { title: "Material", dataIndex: "materialAmount", key: "material", width: 110, align: "right" as const, render: costCell },
    { title: manpowerColumnTitle, dataIndex: "manpowerAmount", key: "manpower", width: 120, align: "right" as const, render: costCell },
    { title: "Machinery", dataIndex: "machineryAmount", key: "machinery", width: 110, align: "right" as const, render: costCell },
    { title: "Outflow", dataIndex: "totalOutflow", key: "outflow", width: 110, align: "right" as const, render: boldCostCell },
    { title: "Margin", dataIndex: "margin", key: "margin", width: 110, align: "right" as const, render: marginCell },
  ];

  return (
    <Table
      size="small"
      rowKey="taskId"
      loading={loading}
      columns={columns}
      dataSource={rows}
      scroll={{ x: 1280 }}
      pagination={{
        current: page,
        pageSize: PAGE_SIZE,
        total: count,
        showSizeChanger: false,
        hideOnSinglePage: true,
        showTotal: (total) => `${total} tasks`,
        onChange: (nextPage) => setPage(nextPage),
      }}
    />
  );
};

const MonthlyPnlMonthDetailPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const search = new URLSearchParams(location.search);

  const state = (location.state as any) || {};
  const projectId = state.id ?? params.projectId;
  const projectNameFromState = state.projectName;
  const wbsName = useWbsName();
  const month = search.get("month") || "";
  const initialTab = (search.get("tab") as PnlDetailTab) || "tasks";
  const phaseId = state.phaseId as number | undefined;
  const baseActivityId = state.activityId as number | undefined;

  const [tab, setTab] = useState<PnlDetailTab>(initialTab);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);

  const [taskCount, setTaskCount] = useState(0);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [activityRollup, setActivityRollup] = useState<any[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);

  // Fullscreen kit (pattern: the top sheet / ThreeMConsolidation). Element
  // fullscreen dies on route changes, so this page re-claims it on arrival
  // when the navigation state carries fullscreen: true, and hands it back the
  // same way via goBack.
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  useEffect(() => {
    if (state.fullscreen && !document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.().catch(() => {
        /* no gesture left — degrade to normal view */
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      containerRef.current?.requestFullscreen?.().catch(() => {
        message.warning("Fullscreen is not supported in this browser");
      });
    }
  };

  const popupContainer = () => containerRef.current || document.body;

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({ ...item, linkState: location.state }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Item tabs + month totals (one bounded fetch).
  useEffect(() => {
    if (!projectId || !month) return;
    let cancelled = false;
    setDetailLoading(true);
    threeMReportApis
      .getMonthlyPnlMonthDetail(projectId, month, { phaseId, activityId: baseActivityId })
      .then((response) => {
        if (cancelled) return;
        if (response?.status) setDetail(response?.data || {});
        else message.error(response?.message || "Failed to load month detail");
      })
      .catch(() => !cancelled && message.error("Failed to load month detail"))
      .finally(() => !cancelled && setDetailLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, month, phaseId, baseActivityId, refreshTick]);

  // Activity rollup + total count for the Tasks tab (the task rows themselves
  // load per activity, on expand — pageSize 1 keeps this fetch tiny).
  useEffect(() => {
    if (!projectId || !month) return;
    let cancelled = false;
    setTasksLoading(true);
    threeMReportApis
      .getMonthlyPnlMonthTasks(projectId, month, {
        phaseId,
        activityId: baseActivityId,
        page: 1,
        pageSize: 1,
      })
      .then((response) => {
        if (cancelled) return;
        if (response?.status) {
          const payload = response?.data || {};
          setTaskCount(num(payload.count));
          setActivityRollup(payload.activityRollup || []);
        } else {
          message.error(response?.message || "Failed to load month tasks");
        }
      })
      .catch(() => !cancelled && message.error("Failed to load month tasks"))
      .finally(() => !cancelled && setTasksLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, month, phaseId, baseActivityId, refreshTick]);

  const projectName = detail?.projectName || projectNameFromState || "Project";
  const monthTotals = detail?.monthTotals;

  const goBack = () =>
    navigate(`/pms/project/monthly-pnl/${projectId}`, {
      state: { ...state, fullscreen: isFullscreen },
    });

  // Outer table of the Tasks tab: one row per activity with the month's
  // inflow / 3M split / outflow / margin; expanding loads that activity's
  // tasks (server-paginated, see ActivityTasksTable).
  const activityColumns: any[] = [
    {
      title: "Activity",
      key: "activity",
      ellipsis: true,
      render: (_: any, row: any) => (
        <span style={{ fontWeight: 600, color: "#022c57" }}>
          {wbsName(row.activityName)}{" "}
          <span style={{ color: "#8094ae", fontWeight: 400 }}>({row.taskCount})</span>
        </span>
      ),
    },
    { title: "Inflow", dataIndex: "inflowAmount", key: "inflow", width: 120, align: "right" as const, render: costCell },
    { title: "Material", dataIndex: "materialAmount", key: "material", width: 120, align: "right" as const, render: costCell },
    { title: manpowerColumnTitle, dataIndex: "manpowerAmount", key: "manpower", width: 130, align: "right" as const, render: costCell },
    { title: "Machinery", dataIndex: "machineryAmount", key: "machinery", width: 120, align: "right" as const, render: costCell },
    { title: "Outflow", dataIndex: "totalOutflow", key: "outflow", width: 120, align: "right" as const, render: boldCostCell },
    { title: "Margin", dataIndex: "margin", key: "margin", width: 120, align: "right" as const, render: marginCell },
  ];

  const itemTable = (
    rows: any[],
    kind: "materials" | "manpower" | "machinery",
    insideHeadGroup = false
  ) => {
    const columns: any[] = [];
    if (kind === "materials") {
      columns.push(
        {
          title: "Material",
          key: "name",
          ellipsis: true,
          render: (_: any, row: any) => (
            <span>
              {row.materialName}{" "}
              {row.materialCode ? <Tag style={{ marginLeft: 4 }}>{row.materialCode}</Tag> : null}
            </span>
          ),
        },
        { title: "UOM", dataIndex: "uomName", key: "uom", width: 90 }
      );
    } else {
      // Source/Head columns only in the flat view — the head-grouped view
      // carries them on the group row instead.
      if (!insideHeadGroup) {
        columns.push(
          {
            title: "Source",
            dataIndex: "source",
            key: "source",
            width: 90,
            render: (value: string) => (
              <Tag color={value === "PRW" || value === "NORM" ? "blue" : undefined}>{value}</Tag>
            ),
          },
          ...(kind === "machinery"
            ? [{ title: "Head", dataIndex: "head", key: "head", width: 110, ellipsis: true }]
            : [])
        );
      }
      columns.push(
        {
          title: "Item",
          key: "name",
          ellipsis: true,
          render: (_: any, row: any) => (
            <span>
              {row.itemName}{" "}
              {row.itemCode ? <Tag style={{ marginLeft: 4 }}>{row.itemCode}</Tag> : null}
            </span>
          ),
        },
        { title: "UOM", dataIndex: "uomName", key: "uom", width: 90 }
      );
    }
    columns.push(
      {
        title: "Month Qty",
        dataIndex: "monthQuantity",
        key: "qty",
        width: 120,
        align: "right" as const,
        render: (value: string) => (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtQty(value)}</span>
        ),
      },
      {
        // Effective rate this month: what the item actually cost per unit once
        // the month's share is applied — the same column, and the same
        // null-denominator rule, as the 3M Month Breakdown drawer, which reads
        // this very endpoint. The two screens must not disagree.
        title: "Rate",
        key: "rate",
        width: 120,
        align: "right" as const,
        render: (_: any, row: any) => {
          const rate = perUnit(num(row.monthCost), num(row.monthQuantity));
          if (rate === null) return <span style={{ color: "#bfbfbf" }}>—</span>;
          return (
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {fmtINR(rate)}
              {row.uomName ? (
                <span style={{ color: "#8c8c8c", fontSize: 12 }}>
                  /{row.uomName}
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        title: "Month Cost",
        dataIndex: "monthCost",
        key: "cost",
        width: 130,
        align: "right" as const,
        render: (value: string) => (
          <Tooltip title={fmtINR(value)}>
            <b style={{ fontVariantNumeric: "tabular-nums", color: "#022c57" }}>
              {fmtINRCompact(value)}
            </b>
          </Tooltip>
        ),
      },
      { title: "Tasks", dataIndex: "taskCount", key: "tasks", width: 80, align: "right" as const }
    );
    return (
      <Table
        size="small"
        rowKey={(row: any, index?: number) => `${kind}-${index}`}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 25, hideOnSinglePage: true, showSizeChanger: false }}
      />
    );
  };

  // Head-wise grouping: Manpower by its two heads (PRW / Others), Machinery by
  // rate-card head (MUCKING, ... + Tools & Machinery). Group rows carry the
  // head's month cost; expanding shows the item rows (all expanded initially).
  const headGroupedTable = (rows: any[], kind: "manpower" | "machinery") => {
    const headOf = (row: any) =>
      kind === "manpower"
        ? row.source === "PRW"
          ? "PRW / Sub-Contract"
          : "Others (Task Resources)"
        : row.head === "TOOLS"
        ? "Tools & Machinery"
        : row.head === "EQUIPMENT"
        ? "Equipment Norms"
        : row.head || "Unassigned";
    const groups = new Map<
      string,
      { head: string; items: any[]; cost: number; qty: number; uoms: Set<string> }
    >();
    rows.forEach((row) => {
      const head = headOf(row);
      if (!groups.has(head))
        groups.set(head, {
          head,
          items: [],
          cost: 0,
          qty: 0,
          uoms: new Set<string>(),
        });
      const group = groups.get(head)!;
      group.items.push(row);
      group.cost += num(row.monthCost);
      // ₹ always sum; a QUANTITY only means something when the group's items
      // agree on a unit, so collect the units and let the rate cell decide.
      // Summing a Cum and a Metre would give a number with no unit to label.
      group.qty += num(row.monthQuantity);
      if (row.uomName) group.uoms.add(row.uomName);
    });
    const dataSource = Array.from(groups.values()).sort((a, b) => b.cost - a.cost);
    const totalCost = dataSource.reduce((total, group) => total + group.cost, 0);
    return (
      <Table
        size="small"
        // remount when data lands so defaultExpandAllRows applies
        key={`${kind}-${rows.length}`}
        rowKey="head"
        dataSource={dataSource}
        pagination={false}
        columns={[
          {
            title: "Head",
            dataIndex: "head",
            key: "head",
            render: (value: string) => (
              <span style={{ fontWeight: 600, color: "#022c57" }}>{value}</span>
            ),
          },
          {
            title: "Items",
            key: "items",
            width: 90,
            align: "right" as const,
            render: (_: any, group: any) => group.items.length,
          },
          {
            title: "Share",
            key: "share",
            width: 140,
            render: (_: any, group: any) => (
              <Progress
                percent={totalCost > 0 ? Math.round((group.cost / totalCost) * 100) : 0}
                size="small"
                strokeColor="#2557d6"
              />
            ),
          },
          {
            // Blank on a mixed-unit head rather than printing a rate with no
            // unit to label it — the same rule the 3M trees use for mixed-UOM
            // parents. A "Mixed" chip says why, so a blank never reads as a bug.
            title: "Rate",
            key: "rate",
            width: 130,
            align: "right" as const,
            render: (_: any, group: any) => {
              if (group.uoms.size > 1)
                return (
                  <Tooltip title="This head's items are measured in different units, so a combined rate would have no unit to label it">
                    <Tag style={{ marginRight: 0 }}>Mixed</Tag>
                  </Tooltip>
                );
              const rate = perUnit(group.cost, group.qty);
              if (rate === null)
                return <span style={{ color: "#bfbfbf" }}>—</span>;
              const uom = group.uoms.size === 1 ? [...group.uoms][0] : "";
              return (
                <span style={{ fontVariantNumeric: "tabular-nums" }}>
                  {fmtINR(rate)}
                  {uom ? (
                    <span style={{ color: "#8c8c8c", fontSize: 12 }}>/{uom}</span>
                  ) : null}
                </span>
              );
            },
          },
          {
            title: "Month Cost",
            key: "cost",
            width: 140,
            align: "right" as const,
            render: (_: any, group: any) => boldCostCell(group.cost),
          },
        ]}
        expandable={{
          defaultExpandAllRows: true,
          expandedRowRender: (group: any) => itemTable(group.items, kind, true),
        }}
      />
    );
  };

  if (!projectId || !month) {
    return (
      <Content>
        <Empty description="Missing project or month context" style={{ padding: "80px 0" }} />
      </Content>
    );
  }

  return (
    <>
      <Head title="Top Sheet — Month Detail" />
      <Content>
        <div
          ref={containerRef}
          style={{
            position: "relative",
            ...(isFullscreen
              ? { background: "#f5f6fa", overflowY: "auto", padding: 16 }
              : {}),
          }}
        >
        <ConfigProvider getPopupContainer={popupContainer}>
        <Card className="card-bordered" style={{ minHeight: "85vh" }}>
          <div className="card-inner card-inner-lg">
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div>
                <Button
                  type="link"
                  icon={<ArrowLeftOutlined />}
                  style={{ paddingLeft: 0 }}
                  onClick={goBack}
                >
                  Top Sheet
                </Button>
                <h5 style={{ fontWeight: 600, marginBottom: 2 }}>
                  {fmtMonthLong(month)} — P&L Detail
                </h5>
                <span style={{ color: "#8c8c8c", fontSize: 13 }}>
                  {projectName} · every amount is the month's share of task costs
                </span>
              </div>
              <Space wrap>
                {monthTotals && (
                  <>
                    {statChip("Inflow", num(monthTotals.totalInflow), "#022c57")}
                    {statChip("Outflow", num(monthTotals.totalOutflow), "#b46a12")}
                    {statChip(
                      "Margin",
                      num(monthTotals.margin),
                      num(monthTotals.margin) >= 0 ? "#237804" : "#cf1322"
                    )}
                  </>
                )}
                <Button
                  icon={
                    isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />
                  }
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? "Exit Full Screen" : "Full Screen"}
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => setRefreshTick((t) => t + 1)}
                  disabled={detailLoading || tasksLoading}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            {detailLoading && !detail ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
              <Tabs
                activeKey={tab}
                onChange={(key) => setTab(key as PnlDetailTab)}
                items={[
                  {
                    key: "tasks",
                    label: `Tasks (${taskCount})`,
                    children: (
                      <Spin spinning={tasksLoading}>
                        <p style={{ color: "#8c8c8c", fontSize: 12, marginBottom: 8 }}>
                          Activity-wise breakdown, biggest outflow first — expand an
                          activity to see its task-wise breakdown for this month.
                        </p>
                        <Table
                          size="small"
                          rowKey="activityId"
                          columns={activityColumns}
                          dataSource={activityRollup}
                          pagination={false}
                          scroll={{ x: 1080 }}
                          expandable={{
                            expandedRowRender: (activity: any) => (
                              <ActivityTasksTable
                                projectId={projectId}
                                month={month}
                                phaseId={phaseId}
                                activityId={activity.activityId}
                              />
                            ),
                          }}
                        />
                      </Spin>
                    ),
                  },
                  {
                    key: "materials",
                    label: `Materials (${detail?.materials?.length || 0})`,
                    children: (
                      <>
                        {itemTable(detail?.materials || [], "materials")}
                        <p style={{ color: "#8c8c8c", fontSize: 12, marginTop: 8 }}>
                          Quantities are BOQ/norm quantities scaled by each task's month share.
                        </p>
                      </>
                    ),
                  },
                  {
                    key: "manpower",
                    label: `Manpower (${detail?.manpower?.length || 0})`,
                    children: headGroupedTable(detail?.manpower || [], "manpower"),
                  },
                  {
                    key: "machinery",
                    label: `Machinery (${detail?.machinery?.length || 0})`,
                    children: (
                      <>
                        {headGroupedTable(detail?.machinery || [], "machinery")}
                        <p style={{ color: "#8c8c8c", fontSize: 12, marginTop: 8 }}>
                          Tools & Machinery rows include their consumables' cost share.
                        </p>
                      </>
                    ),
                  },
                ]}
              />
            )}
          </div>
        </Card>
        </ConfigProvider>
        </div>
      </Content>
    </>
  );
};

export default MonthlyPnlMonthDetailPage;
